import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type {
  ClientFormData,
  ClientFormFieldData,
  FormAnalyticsData,
  FormFieldCatalogData,
} from '@/types/api';

/**
 * Client website forms: the field map, the ingest secret, and the endpoint the
 * client's developer posts to. Admin-gated.
 */

export type ClientFormInput = {
  name?: string;
  slug?: string;
  status?: string;
  honeypot_key?: string | null;
  fields?: FieldDraft[];
};

/** The editor's working shape — `max` and `role` are empty until chosen. */
export type FieldDraft = {
  key: string;
  label: string;
  type: string;
  role?: string | null;
  required?: boolean;
  include_in_summary?: boolean;
  options?: string[];
  accept?: string[];
  max?: number | null;
  /**
   * Client-only. Marks a cell whose value came from detection and has not been
   * touched, so the editor can flag what to double-check. Stripped before save.
   */
  detectedType?: boolean;
  detectedRole?: boolean;
  /** Client-only. False for a row the user added by hand, whose key is editable. */
  discovered?: boolean;
};

/**
 * The exact shape the API stores. Built explicitly rather than by spreading the
 * draft, so the client-only markers (detectedType, detectedRole, discovered)
 * can never leak into a saved field map.
 */
export function toPayloadFields(rows: FieldDraft[]) {
  return rows.map((row) => ({
    key: row.key,
    label: row.label,
    type: row.type,
    role: row.role || null,
    required: row.required ?? false,
    include_in_summary: row.include_in_summary ?? false,
    options: row.options ?? [],
    accept: row.accept ?? [],
    max: row.max ?? null,
  }));
}

const FORMS_KEY = ['client-forms'] as const;
const FORM_KEY = (id: number) => ['client-forms', id] as const;
const SITE_FORMS_KEY = (siteId: number) => ['sites', siteId, 'forms'] as const;

/** Static for the session — the catalog only changes when the enum does. */
export function useFieldCatalog() {
  return useQuery({
    queryKey: ['form-field-types'],
    staleTime: 60 * 60_000,
    queryFn: async (): Promise<FormFieldCatalogData> => {
      const { data } = await api.get<FormFieldCatalogData>('/v1/form-field-types');
      return data;
    },
  });
}

export function useForms(filters: { client_id?: number; site_id?: number; status?: string } = {}) {
  return useQuery({
    queryKey: [...FORMS_KEY, filters],
    queryFn: async (): Promise<Paginated<ClientFormData>> => {
      const { data } = await api.get<Paginated<ClientFormData>>('/v1/forms', { params: filters });
      return data;
    },
  });
}

export function useSiteForms(siteId: number | undefined) {
  return useQuery({
    queryKey: siteId ? SITE_FORMS_KEY(siteId) : ['sites', 'none', 'forms'],
    enabled: siteId != null,
    queryFn: async (): Promise<Paginated<ClientFormData>> => {
      const { data } = await api.get<Paginated<ClientFormData>>(`/v1/sites/${siteId}/forms`);
      return data;
    },
  });
}

export function useClientForm(id: number | undefined) {
  return useQuery({
    queryKey: id ? FORM_KEY(id) : ['client-forms', 'none'],
    enabled: id != null,
    queryFn: async (): Promise<ClientFormData> => {
      const { data } = await api.get<ClientFormData>(`/v1/forms/${id}`);
      return data;
    },
  });
}

export function useCreateForm(siteId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClientFormInput): Promise<ClientFormData> => {
      await primeCsrf();
      const { data } = await api.post<ClientFormData>(`/v1/sites/${siteId}/forms`, input);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SITE_FORMS_KEY(siteId) });
      void qc.invalidateQueries({ queryKey: FORMS_KEY });
    },
  });
}

export function useUpdateForm(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: ClientFormInput): Promise<ClientFormData> => {
      await primeCsrf();
      const { data } = await api.patch<ClientFormData>(`/v1/forms/${id}`, patch);
      return data;
    },
    onSuccess: (form) => {
      qc.setQueryData(FORM_KEY(id), form);
      void qc.invalidateQueries({ queryKey: FORMS_KEY });
      void qc.invalidateQueries({ queryKey: SITE_FORMS_KEY(form.site_id) });
    },
  });
}

export function useDeleteForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/forms/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: FORMS_KEY }),
  });
}

/** The plaintext comes back once. Show it immediately or it is gone. */
export function useRegenerateFormSecret(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ secret: string; form: ClientFormData }> => {
      await primeCsrf();
      const { data } = await api.post<{ secret: string; form: ClientFormData }>(
        `/v1/forms/${id}/regenerate-secret`,
      );
      return data;
    },
    onSuccess: ({ form }) => qc.setQueryData(FORM_KEY(id), form),
  });
}

/** Server field shape -> editor draft. */
export function toDraft(field: ClientFormFieldData): FieldDraft {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    role: field.role ?? '',
    required: field.required,
    include_in_summary: field.include_in_summary,
    options: field.options ?? [],
    accept: field.accept ?? [],
    max: field.max,
    // Already saved, so its key is what incoming submissions are matched on.
    discovered: true,
  };
}

/**
 * Submission counts over time for one form, with the previous equivalent
 * period alongside. Buckets arrive gap-filled, so an empty period is a zero
 * rather than a missing point.
 */
export function useFormAnalytics(
  id: number | undefined,
  params: { granularity?: string; periods?: number } = {},
) {
  return useQuery({
    queryKey: ['client-forms', id, 'analytics', params],
    enabled: id != null,
    queryFn: async (): Promise<FormAnalyticsData> => {
      const { data } = await api.get<FormAnalyticsData>(`/v1/forms/${id}/analytics`, { params });
      return data;
    },
  });
}

/**
 * Forms the caller can see in the portal, scoped server-side by the
 * FormSubmissions grant. Admins get all of them, so the portal screen is
 * testable from an admin session.
 */
export function useMyForms() {
  return useQuery({
    queryKey: ['portal', 'forms'],
    queryFn: async (): Promise<Paginated<ClientFormData>> => {
      const { data } = await api.get<Paginated<ClientFormData>>('/v1/portal/forms');
      return data;
    },
  });
}
